import React, {useMemo} from 'react';


import {SelectionHeader, SelectionRow} from 'src/components/data-table-selection';
import {AccountLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {MAX_SELECTED_ENTITIES, NO_RESULTS_MESSAGE, SEARCH_RESULT_HEADER_HEIGHT} from 'src/constants';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {
    Cell,
    type CellOptions,
    type Column,
    DataTable,
    type HeaderCellOptions,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';

import {
    ConferenceUsersCell,
    CreditAlertsUsersCell,
    CrmEnabledUsersCell,
    DealCLoudIntegrationCell,
    DefaultCell,
    DisabledUsersCell,
    HubSpotIntegrationCell,
    LicenseEndDateCell,
    LicenseSeatsCell,
    LicenseStartDateCell,
    LicenseTypeCell,
    LicensedEnabledUsersCell,
    MobileEnabledUsersCell,
    MsDynamicsIntegrationCell,
    NoAccessUsersCell,
    ParentAccountCell,
    PrimaryManagerCell,
    SalesforceIntegrationCell,
    SsoEnabledCell,
    SsoMandatoryCell,
} from './components/cells';
import {
    AccountNameHeader,
    ConferenceUsersHeader,
    CreditAlertsUsersHeader,
    CrmEnabledUsersHeader,
    DealCloudIntegrationHeader,
    DefaultHeader,
    DisabledUsersHeader,
    EntityTypeHeader,
    HubSpotIntegrationHeader,
    LicenseEndDateHeader,
    LicenseSeatsHeader,
    LicenseStartDateHeader,
    LicenseTypeHeader,
    LicensedEnabledUsersHeader,
    MobileEnabledUsersHeader,
    MsDynamicsIntegrationHeader,
    NoAccessUsersHeader,
    ParentAccountHeader,
    PrimaryManagerHeader,
    SalesforceIntegrationHeader,
    SortableHeader,
    SsoEnabledHeader,
    SsoMandatoryHeader,
} from './components/headers';
import {AccountSearchResultPagination} from './components/pagination';
import {
    COLUMNS,
    COLUMNS_DATA,
    SELECTION_KEY,
    SORTING_ERROR,
} from './constants';
import {useGetAccountSearchResult} from './hooks/useGetAccountSearchResult';
import {useGetColumnValue} from './hooks/useGetColumnValue';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    place: string,
    activeSearchTemplate: UserSearchTemplate,
    getAllEntityIds: () => Promise<{
        type: string,
        payload: {
            data: string[] | number[],
            success: true,
            error: false,
        } | {
            data: ApiError,
            success: false,
            error: true,
        },
    }>,
};

export const AccountSearchResultView = ({place, activeSearchTemplate, getAllEntityIds}: Props) => {
    const {getColumnValue} = useGetColumnValue();

    const {result, pagination, loadingError} = useGetAccountSearchResult();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const renderedRows = useMemo(() => {
        if (!result || !Array.isArray(result.data)) {
            return undefined;
        }

        return result.data.map(account => ({
            id: account.accountId,
            columnValues: Object.entries(activeSearchTemplate.columns)
                .map(([code, order]) => ({code, order}))
                .sort((a, b) => a.order - b.order)
                .reduce((acc, column) => ({
                    ...acc,
                    [COLUMNS[column.code]]: getColumnValue(COLUMNS[column.code], account),
                }), {}),
        }));
    }, [result, activeSearchTemplate, getColumnValue]);

    const maxRowNumber = useMemo(() => (
        getPaginatedRowNumber({...pagination, index: renderedRows?.length})
    ), [pagination, renderedRows]);
    const rows = useMemo(() => result?.data.map(({accountId}) => accountId) || [], [result]);
    const totalCount = useMemo(() => result?.totalCount || 0, [result]);

    return (
        <ServerResponseHandler serverError={loadingError?.message === SORTING_ERROR ? null : loadingError}>
            <BoxLoading loading={!result}>
                <DataTable
                    data={{
                        rows: renderedRows,
                        columns: Object.entries(activeSearchTemplate.columns)
                            .map(([code, order]) => ({code, order}))
                            .sort((a, b) => a.order - b.order)
                            .map(({code}) => {
                                const column =
                                    COLUMNS_DATA.find(({columnId}) => COLUMNS[code] === columnId) || {
                                        columnId: COLUMNS[code],
                                        fixed: false,
                                        defaultWidth: 200,
                                        minWidth: 200,
                                        maxWidth: 400,
                                    };

                                return ({
                                    ...column,
                                    width: getColumnWidthByColumnId(column.columnId),
                                });
                            }),
                    }}
                    getHeaderTopOffset={() => SEARCH_RESULT_HEADER_HEIGHT}
                    onStopResize={onStopResize}
                    headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                        const wrapper = (children: React.ReactNode) => (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    {children}
                                </TableHeader>
                            </Cell>
                        );

                        switch (column.columnId) {
                            case COLUMNS.ACCOUNT_NAME:
                                return (
                                    <Cell
                                        {...cellOptions}
                                        key={`${column.columnId}`}
                                        column={column}
                                        resizable={true}>
                                        <MainColumnHeader
                                            selection={(
                                                <SelectionHeader
                                                    selectionKey={SELECTION_KEY}
                                                    entityIds={rows}
                                                    totalCount={result?.totalCount || 0}
                                                    maxSelected={MAX_SELECTED_ENTITIES}
                                                    getEntityIds={getAllEntityIds}
                                                />
                                            )}
                                            maxRowNumber={maxRowNumber}>
                                            <AccountNameHeader/>
                                        </MainColumnHeader>
                                    </Cell>
                                );
                            case COLUMNS.PARENT_ACCOUNT_NAME:
                                return wrapper(<ParentAccountHeader/>);
                            case COLUMNS.LICENSED_ENABLED_USERS:
                                return wrapper(<LicensedEnabledUsersHeader/>);
                            case COLUMNS.MOBILE_ENABLED_USERS:
                                return wrapper(<MobileEnabledUsersHeader/>);
                            case COLUMNS.CRM_ENABLED_USERS:
                                return wrapper(<CrmEnabledUsersHeader/>);
                            case COLUMNS.DISABLED_NEWSLETTER_USERS:
                                return wrapper(<DisabledUsersHeader/>);
                            case COLUMNS.NO_ACCESS_USERS:
                                return wrapper(<NoAccessUsersHeader/>);
                            case COLUMNS.CONFERENCE_USERS:
                                return wrapper(<ConferenceUsersHeader/>);
                            case COLUMNS.CREDIT_ALERTS_ENABLED_USERS:
                                return wrapper(<CreditAlertsUsersHeader/>);
                            case COLUMNS.SALESFORCE_INTEGRATION:
                                return wrapper(<SalesforceIntegrationHeader/>);
                            case COLUMNS.MS_DYNAMICS_INTEGRATION:
                                return wrapper(<MsDynamicsIntegrationHeader/>);
                            case COLUMNS.DEAL_CLOUD_INTEGRATION:
                                return wrapper(<DealCloudIntegrationHeader/>);
                            case COLUMNS.HUBSPOT_INTEGRATION:
                                return wrapper(<HubSpotIntegrationHeader/>);
                            case COLUMNS.SSO_ENABLED:
                                return wrapper(<SsoEnabledHeader/>);
                            case COLUMNS.SSO_MANDATORY:
                                return wrapper(<SsoMandatoryHeader/>);
                            case COLUMNS.PRIMARY_ACCOUNT_MANAGER:
                                return wrapper(<PrimaryManagerHeader/>);
                            case COLUMNS.LICENSE_TYPE:
                                return wrapper(<LicenseTypeHeader/>);
                            case COLUMNS.LICENSE_START_DATE:
                                return wrapper(<LicenseStartDateHeader/>);
                            case COLUMNS.LICENSE_END_DATE:
                                return wrapper(<LicenseEndDateHeader/>);
                            case COLUMNS.LICENSE_SEATS:
                                return wrapper(<LicenseSeatsHeader/>);
                            case COLUMNS.ENTITY_TYPE:
                                return wrapper(<EntityTypeHeader/>);
                            case COLUMNS.IS_ACCOUNT_DELETED:
                            case COLUMNS.ACCOUNT_COUNTRY:
                            case COLUMNS.ACCOUNT_STATUS:
                            case COLUMNS.ACCOUNT_SEARCH_ID:
                                return wrapper(<SortableHeader columnId={column.columnId}/>);
                            default:
                                return wrapper(<DefaultHeader columnId={column.columnId}/>);
                        }
                    }}
                    renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                        const {rowIndex, column: {columnId}} = rowsParams;
                        const value = rowsParams.row.columnValues[columnId] as string;
                        const rowId = +rowsParams.row.id;
                        const column = rowsParams.column;
                        const cellKey = rowIndex + columnId.toString();
                        const rowNumber = getPaginatedRowNumber({...pagination, index: rowIndex + 1});

                        const wrapper = (children: React.ReactNode) => (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                {children}
                            </Cell>
                        );

                        switch (columnId) {
                            case COLUMNS.ACCOUNT_NAME:
                                return (
                                    <MainColumnCell
                                        key={cellKey}
                                        {...{
                                            selection: (
                                                <SelectionRow
                                                    rows={rows}
                                                    index={rowIndex}
                                                    entityId={rowId}
                                                    selectionKey={SELECTION_KEY}
                                                    totalCount={totalCount}
                                                />
                                            ),
                                            rowId,
                                            maxRowNumber,
                                            rowNumber,
                                            cellOptions: {...cellOptions, column},
                                        }}>
                                        <AccountLink accountId={rowId} place={place} accountName={value as string}/>
                                    </MainColumnCell>
                                );
                            case COLUMNS.PARENT_ACCOUNT_NAME:
                                return wrapper(<ParentAccountCell parentAccountId={Number(value)}/>);
                            case COLUMNS.LICENSED_ENABLED_USERS:
                                return wrapper(<LicensedEnabledUsersCell accountId={rowId}/>);
                            case COLUMNS.MOBILE_ENABLED_USERS:
                                return wrapper(<MobileEnabledUsersCell accountId={rowId}/>);
                            case COLUMNS.CRM_ENABLED_USERS:
                                return wrapper(<CrmEnabledUsersCell accountId={rowId}/>);
                            case COLUMNS.DISABLED_NEWSLETTER_USERS:
                                return wrapper(<DisabledUsersCell accountId={rowId}/>);
                            case COLUMNS.NO_ACCESS_USERS:
                                return wrapper(<NoAccessUsersCell accountId={rowId}/>);
                            case COLUMNS.CONFERENCE_USERS:
                                return wrapper(<ConferenceUsersCell accountId={rowId}/>);
                            case COLUMNS.CREDIT_ALERTS_ENABLED_USERS:
                                return wrapper(<CreditAlertsUsersCell accountId={rowId}/>);
                            case COLUMNS.SALESFORCE_INTEGRATION:
                                return wrapper(<SalesforceIntegrationCell accountId={rowId}/>);
                            case COLUMNS.MS_DYNAMICS_INTEGRATION:
                                return wrapper(<MsDynamicsIntegrationCell accountId={rowId}/>);
                            case COLUMNS.DEAL_CLOUD_INTEGRATION:
                                return wrapper(<DealCLoudIntegrationCell accountId={rowId}/>);
                            case COLUMNS.HUBSPOT_INTEGRATION:
                                return wrapper(<HubSpotIntegrationCell accountId={rowId}/>);
                            case COLUMNS.SSO_ENABLED:
                                return wrapper(<SsoEnabledCell accountId={rowId}/>);
                            case COLUMNS.SSO_MANDATORY:
                                return wrapper(<SsoMandatoryCell accountId={rowId}/>);
                            case COLUMNS.PRIMARY_ACCOUNT_MANAGER:
                                return wrapper(<PrimaryManagerCell accountId={rowId}/>);
                            case COLUMNS.LICENSE_TYPE:
                                return wrapper(<LicenseTypeCell accountId={rowId}/>);
                            case COLUMNS.LICENSE_START_DATE:
                                return wrapper(<LicenseStartDateCell accountId={rowId}/>);
                            case COLUMNS.LICENSE_END_DATE:
                                return wrapper(<LicenseEndDateCell accountId={rowId}/>);
                            case COLUMNS.LICENSE_SEATS:
                                return wrapper(<LicenseSeatsCell accountId={rowId}/>);
                            default:
                                return wrapper(<DefaultCell value={String(value)}/>);
                        }
                    }}
                    paginationStripe={(
                        <AccountSearchResultPagination totalCount={result?.totalCount || 0}/>
                    )}
                    showNoDataMsg={!rows?.length}
                    tableMessage={NO_RESULTS_MESSAGE}
                />
            </BoxLoading>
        </ServerResponseHandler>
    );
};
